import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import SteppableProgressBar from '@r1-frontend/ui-react/organisms/SteppableProgressBar';

import { MEGABIT_AVAIL_CODES_LIST, MEGABIT_SPEED } from '~/src/features/full-buy/consts';
import { setStep } from '~/src/features/tariffBuilder/actions';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getSelectedEntity from '~/src/features/tariffBuilder/selectors/getSelectedEntity';
import selectCurrentTariff from '~/src/features/tariffBuilder/selectors/selectCurrentTariff';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';
import { TState } from '~/src/store';

const ProgressBarContainer = (): JSX.Element => {
    const currentStep = useCtxSelector(state => (state.currentStep));
    const { speed, currentTariff, requestId } = useCtxSelector(state => ({
        speed: getSelectedEntity(state),
        currentTariff: selectCurrentTariff(state),
        requestId: state.requestId,
    }));
    const { isHouseConnected, gigabitAvail } = useSelector((state: TState) => (state.contactAddress.verifiedAddressData));
    const dispatch = useCtxDispatch();

    const progress = (currentStep - ESteps.FillAddress + 1) / (ESteps.Final - ESteps.FillAddress);

    const canConnectAfterCheckedAddress = useMemo(() => {
        const gigabitMayConnect = MEGABIT_AVAIL_CODES_LIST.includes(gigabitAvail);

        const requiredGigaSpeed = !!speed && (Number(speed) > MEGABIT_SPEED);

        return requiredGigaSpeed
            ? (isHouseConnected && gigabitMayConnect)
            : isHouseConnected;
    }, [isHouseConnected, gigabitAvail, speed]);

    const onClickHandler = () => {
        if (!currentTariff) {
            dispatch(setStep(currentStep - 1));
            return;
        }
        if (currentStep === ESteps.FillContacts) {
            dispatch(setStep(ESteps.Equipment));
        } else {
            dispatch(setStep(currentStep - 1));
        }
    };

    const dynamicProps: { title: string, imgName?: string } = useMemo(() => {
        switch (currentStep) {
            case ESteps.FillContacts: {
                return {
                    title: 'Оформление заявки',
                    onClick: onClickHandler,
                };
            }
            case ESteps.FillAddress: {
                return {
                    title: canConnectAfterCheckedAddress ? 'Уже прокладываем маршрут...' : 'Проверка адреса',
                    imgName: canConnectAfterCheckedAddress ? 'dog-walking.gif' : '',
                    onClick: onClickHandler,
                };
            }
            case ESteps.CreateAgreement: {
                return {
                    title: 'Осталось совсем чуть-чуть...',
                };
            }
            case ESteps.SelectVisitData: {
                return {
                    title: 'Остался последний шаг',
                };
            }
            case ESteps.Final: {
                return {
                    title: (currentTariff && !requestId) ? '' : 'Вы молодец! Заявка отправлена.',
                    imgName: 'lightblue-paper-airplane.gif',
                };
            }
            default: {
                return {
                    title: '',
                };
            }
        }
    }, [currentStep, canConnectAfterCheckedAddress, currentTariff, requestId]);

    const props = {
        progress: progress,
        imgPath: '/static/images/full-buy/for-progress-bar',

        ...dynamicProps,
    };

    return (
        <IndentContainer $united={[0, 0, 24]}>
            <SteppableProgressBar {...props}/>
        </IndentContainer>
    );
};

export default ProgressBarContainer;
